import React, { Component, ComponentClass, ComponentType, createElement } from 'react';
import { connect, ResolveThunks } from 'react-redux';

import { AspNetStatusCodeResult } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { RootState } from '../../../../store';
import { closeSettingsPanel } from '../../../../store/actions/appActions';
import { getProjectLayoutModel } from '../../../../store/selectors/getProjectLayoutModel';
import { getSelectedModelForSettingsPanel } from '../../../../store/selectors/settingsPanelSelectors';
import { SaveOptions, SettingsControlsComponent, SettingsProvider, SettingsSaveActions } from './interfaces';
import ErrorPanel from './other/ErrorPanel';
import LoadingSpinnerPanel from './other/LoadingSpinnerPanel';
import { SettingsCloseButton } from './other/SettingsCloseButton';
import SettingsPanelBase from './SettingsPanelBase';
import SaveSettingsButton, { SaveAction } from './SettingsSaveButton';

interface OwnState<T> {
  error?: string;

  isSaving: boolean;

  originalSettingsObject?: T;
  updatedSettingsObject?: T;
}

interface Configuration<T> {
  fetchSettings: SettingsProvider<T>;
  controls: SettingsControlsComponent<T>;

  isViewOnly: boolean;
  disableSavingActualSettings?: boolean;

  onSave: SettingsSaveActions<T>;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
export type SettingsPanelContainerProps<T> = StateProps & DispatchProps & Configuration<T>;

class SettingsPanelContainer<T> extends Component<SettingsPanelContainerProps<T>> {
  readonly state: OwnState<T> = {
    isSaving: false,
  };

  async componentDidMount() {
    // If we're inserting a component design, the settings should already be loaded
    if (this.props.componentDesignInsertionInfo) {
      const settings = this.props.fetchSettings.fromInsertionInfo(this.props.componentDesignInsertionInfo);
      this.setState({ originalSettingsObject: settings });
      return;
    }

    const settingsPromise = this.props.fetchSettings.fromSettingsDatabase(this.props.layoutModelGuid);

    // Usually, settings will already be loaded in redux state or will load very fast (on a good connection).  So we
    //   want to avoid showing a loading spinner unless it's taking a while to load the settings.
    let cleanupSpinner = () => {};
    const spinnerTimeout = setTimeout(() => {
      const spinnerId = this.props.addSpinnerMessage('Loading settings...');
      cleanupSpinner = () => this.props.removeSpinnerMessage(spinnerId);
    }, 200);

    try {
      this.setState({ originalSettingsObject: await settingsPromise });
      clearTimeout(spinnerTimeout);
    } catch (error) {
      if ((error as AspNetStatusCodeResult).status === 404) {
        this.setState({ error: 'Unable to find component settings' });
      } else {
        console.error(error);
      }
    } finally {
      cleanupSpinner();
    }
  }

  getSaveOptions(action: SaveAction): SaveOptions {
    const baseOptions = {
      action,
      modelGuid: this.props.layoutModelGuid,
    };

    if (action === SaveAction.ActualForSingleComponent) {
      return { ...baseOptions, action };
    }

    return {
      ...baseOptions,
      projectGuid: this.props.projectGuid!,
    };
  }

  onSave = async (action: SaveAction) => {
    if (!this.state.updatedSettingsObject) {
      return;
    }

    if (this.state.originalSettingsObject == null) {
      throw new Error(
        'originalSettingsObject is null when updatedSettingsObject is not null. This should never happen.',
      );
    }

    if (this.props.componentDesignInsertionInfo) {
      await this.props.onSave.forInsertionComponent(
        this.state.updatedSettingsObject,
        this.state.originalSettingsObject,
      );
    } else {
      const options = this.getSaveOptions(action);

      this.setState({ isSaving: true });
      const spinnerId = this.props.addSpinnerMessage('Saving settings...');

      try {
        await this.props.onSave.forExistingModel(
          this.state.updatedSettingsObject,
          options,
          this.state.originalSettingsObject,
        );
      } catch (error) {
        this.props.enqueueError('Error saving settings.  Please contact Paragon Customer Support.');

        console.error(error);
        return;
      } finally {
        this.setState({ isSaving: false });
        this.props.removeSpinnerMessage(spinnerId);
      }
    }

    this.setState({ originalSettingsObject: this.state.updatedSettingsObject, updatedSettingsObject: undefined });
    this.props.closeSettingsPanel();
  };

  onChange = (changes: Partial<T>) => {
    this.setState((previousState: OwnState<T>) => {
      if (previousState.originalSettingsObject == null) {
        // This should never happen because the controls should not yet be rendered.
        throw new Error('Attempt made to edit settings before they were loaded.');
      }

      const currentSettings = previousState.updatedSettingsObject ?? previousState.originalSettingsObject;
      return { updatedSettingsObject: { ...currentSettings, ...changes } };
    });
  };

  render() {
    if (this.state.error) {
      return <ErrorPanel error={this.state.error} />;
    }

    if (!this.state.originalSettingsObject) {
      return <LoadingSpinnerPanel />;
    }

    const settingsChanged = !!this.state.updatedSettingsObject && !this.state.isSaving && !this.props.isViewOnly;

    return (
      <SettingsPanelBase
        saveButtons={
          this.props.isViewOnly ? (
            <SettingsCloseButton />
          ) : (
            <SaveSettingsButton
              userVisibleModelType={this.props.userVisibleModelType}
              disableSavingActualSettings={this.props.disableSavingActualSettings}
              enableSaving={settingsChanged}
              onSave={this.onSave}
            />
          )
        }
      >
        {createElement(this.props.controls, {
          isViewOnly: this.props.isViewOnly,

          onChange: this.onChange,
          settingsObject: this.state.updatedSettingsObject ?? this.state.originalSettingsObject,
        })}
      </SettingsPanelBase>
    );
  }
}

const mapStateToProps = (state: RootState) => {
  const selectedModel = getSelectedModelForSettingsPanel(state);

  if (selectedModel == null) {
    throw new Error('A settings container should never be rendered when a model is not selected.');
  }

  const project = getProjectLayoutModel(state);

  const { insertingObjectInfo } = state.application;
  const componentDesignInsertionInfo = insertingObjectInfo?.type === 'componentDesign' ? insertingObjectInfo : null;

  return {
    layoutModelGuid: selectedModel.model.guid,
    userVisibleModelType: selectedModel.type === 'component' ? 'component' : selectedModel.model.type,

    projectGuid: project ? project.guid : null,

    componentDesignInsertionInfo,
  };
};

const actionCreators = {
  closeSettingsPanel,
  enqueueError,
  addSpinnerMessage,
  removeSpinnerMessage,
};

type UnconnectedPanel<T> = ComponentClass<SettingsPanelContainerProps<T>>;
type ConnectedPanel<T> = ComponentType<Configuration<T>>;

export function connectSettingsPanelContainer<T>(component: UnconnectedPanel<T>): ConnectedPanel<T> {
  return connect(mapStateToProps, actionCreators)(component);
}

export default SettingsPanelContainer;
